/**
 * Particle System - handles hit effects, death effects, and other visual particles
 */

import * as THREE from 'three';
import { EventBus } from '@/core/events/EventBus';
import type { World } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { PositionComponent } from '@/game/components/Position';
import type { FactionComponent } from '@/game/components/Faction';
import { FACTIONS } from '@/game/factions/FactionData';
import type { Entity } from '@/types';

interface Particle {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  age: number;
  maxAge: number;
  color: THREE.Color;
  scale: number;
  type: 'hit' | 'death' | 'spawn';
}

const MAX_PARTICLES = 500;

export class ParticleSystem {
  private scene: THREE.Scene;
  private world: World | null = null;
  private particles: Particle[] = [];

  // Instanced mesh for particles
  private geometry: THREE.SphereGeometry;
  private material: THREE.MeshBasicMaterial;
  private instancedMesh: THREE.InstancedMesh;
  private dummy = new THREE.Object3D();
  private colorAttribute: THREE.InstancedBufferAttribute;

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // Create particle geometry (small sphere)
    this.geometry = new THREE.SphereGeometry(0.1, 4, 4);

    // Create material with vertex colors
    this.material = new THREE.MeshBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
    });

    // Create instanced mesh
    this.instancedMesh = new THREE.InstancedMesh(
      this.geometry,
      this.material,
      MAX_PARTICLES
    );
    this.instancedMesh.count = 0;
    this.instancedMesh.frustumCulled = false;
    this.scene.add(this.instancedMesh);

    // Setup instance colors
    const colors = new Float32Array(MAX_PARTICLES * 3);
    this.colorAttribute = new THREE.InstancedBufferAttribute(colors, 3);
    this.geometry.setAttribute('color', this.colorAttribute);

    // Listen for events
    this.setupEventListeners();
  }

  setWorld(world: World): void {
    this.world = world;
  }

  private setupEventListeners(): void {
    // Hit effects
    EventBus.on('unit:attack', (data: { attackerId: Entity; targetId: Entity; damage: number }) => {
      if (!this.world) return;

      const targetPos = this.world.getComponent<PositionComponent>(
        data.targetId,
        ComponentTypes.Position
      );
      if (targetPos) {
        this.spawnHitEffect(targetPos.x, targetPos.y + 0.5, targetPos.z);
      }
    });

    // Death effects
    EventBus.on('unit:death', (data: { entityId: Entity; factionId: string }) => {
      if (!this.world) return;

      const pos = this.world.getComponent<PositionComponent>(
        data.entityId,
        ComponentTypes.Position
      );
      if (pos) {
        const faction = FACTIONS[data.factionId as keyof typeof FACTIONS];
        const color = faction ? new THREE.Color(faction.color) : new THREE.Color(0xffffff);
        this.spawnDeathEffect(pos.x, pos.y + 0.5, pos.z, color);
      }
    });

    // Spawn effects
    EventBus.on('unit:spawn', (data: { entityId: Entity; factionId: string }) => {
      if (!this.world) return;

      const pos = this.world.getComponent<PositionComponent>(
        data.entityId,
        ComponentTypes.Position
      );
      if (pos) {
        const faction = FACTIONS[data.factionId as keyof typeof FACTIONS];
        const color = faction ? new THREE.Color(faction.color) : new THREE.Color(0xffffff);
        this.spawnSpawnEffect(pos.x, pos.y + 0.5, pos.z, color);
      }
    });
  }

  private spawnHitEffect(x: number, y: number, z: number): void {
    // Spawn 5-8 sparks
    const count = 5 + Math.floor(Math.random() * 4);

    for (let i = 0; i < count; i++) {
      if (this.particles.length >= MAX_PARTICLES) break;

      // Random direction
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI * 0.5;
      const speed = 2 + Math.random() * 3;

      this.particles.push({
        x,
        y,
        z,
        vx: Math.cos(theta) * Math.sin(phi) * speed,
        vy: Math.cos(phi) * speed + 2, // Upward bias
        vz: Math.sin(theta) * Math.sin(phi) * speed,
        age: 0,
        maxAge: 0.3 + Math.random() * 0.2,
        color: new THREE.Color().setHSL(0.08 + Math.random() * 0.05, 1, 0.5 + Math.random() * 0.3), // Orange-yellow sparks
        scale: 0.8 + Math.random() * 0.4,
        type: 'hit',
      });
    }
  }

  private spawnDeathEffect(x: number, y: number, z: number, baseColor: THREE.Color): void {
    // Spawn 10-15 particles for death effect
    const count = 10 + Math.floor(Math.random() * 6);

    for (let i = 0; i < count; i++) {
      if (this.particles.length >= MAX_PARTICLES) break;

      // Random direction (more spread out)
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 1 + Math.random() * 2;

      // Slightly varied color
      const color = baseColor.clone();
      color.offsetHSL(0, -0.2 * Math.random(), -0.2 * Math.random());

      this.particles.push({
        x: x + (Math.random() - 0.5) * 0.5,
        y: y + (Math.random() - 0.5) * 0.5,
        z: z + (Math.random() - 0.5) * 0.5,
        vx: Math.cos(theta) * Math.sin(phi) * speed,
        vy: Math.cos(phi) * speed + 1, // Slight upward
        vz: Math.sin(theta) * Math.sin(phi) * speed,
        age: 0,
        maxAge: 0.5 + Math.random() * 0.5,
        color,
        scale: 1 + Math.random() * 0.5,
        type: 'death',
      });
    }
  }

  private spawnSpawnEffect(x: number, y: number, z: number, baseColor: THREE.Color): void {
    // Spawn 6 particles rising upward for spawn effect
    const count = 6;

    for (let i = 0; i < count; i++) {
      if (this.particles.length >= MAX_PARTICLES) break;

      const angle = (i / count) * Math.PI * 2;
      const radius = 0.3;

      const color = baseColor.clone();
      color.offsetHSL(0, 0, 0.3); // Brighter

      this.particles.push({
        x: x + Math.cos(angle) * radius,
        y: y - 0.5,
        z: z + Math.sin(angle) * radius,
        vx: Math.cos(angle) * 0.3,
        vy: 3 + Math.random(),
        vz: Math.sin(angle) * 0.3,
        age: 0,
        maxAge: 0.6 + Math.random() * 0.2,
        color,
        scale: 0.6,
        type: 'spawn',
      });
    }
  }

  update(deltaTime: number): void {
    // Update particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.age += deltaTime;

      if (p.age >= p.maxAge) {
        this.particles.splice(i, 1);
        continue;
      }

      // Apply velocity
      p.x += p.vx * deltaTime;
      p.y += p.vy * deltaTime;
      p.z += p.vz * deltaTime;

      // Apply gravity (less for spawn effects)
      if (p.type !== 'spawn') {
        p.vy -= 9.8 * deltaTime;
      }

      // Apply drag
      p.vx *= 0.98;
      p.vz *= 0.98;
    }

    // Update instanced mesh
    this.instancedMesh.count = this.particles.length;

    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];
      const lifeRatio = 1 - p.age / p.maxAge;

      // Position
      this.dummy.position.set(p.x, p.y, p.z);

      // Scale based on life and particle type
      const scale = p.scale * lifeRatio;
      this.dummy.scale.setScalar(scale);

      this.dummy.updateMatrix();
      this.instancedMesh.setMatrixAt(i, this.dummy.matrix);

      // Color with fade
      const color = p.color.clone();
      this.colorAttribute.setXYZ(i, color.r, color.g, color.b);
    }

    this.instancedMesh.instanceMatrix.needsUpdate = true;
    this.colorAttribute.needsUpdate = true;
  }

  dispose(): void {
    this.scene.remove(this.instancedMesh);
    this.geometry.dispose();
    this.material.dispose();
    this.particles = [];
  }
}
